import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { 
  Box,
  Typography,
  InputAdornment
} from "@mui/material";
import { SearchOutlined as SearchIcon, CalculateOutlined as CalculateIcon } from "@mui/icons-material";
import { SelectWithFilter } from "../../utils";
import usePatientSearchStrategy from "../utils/patient/search/hooks/usePatientSearchStrategy";
import { searchCalculatorsMultilingual } from "../utils/qxmd/calculatorsData";
import CalculatorDialog from "../../components/CalculatorDialog";

const defaultPatient = { id: "", surname: "" };

const SearchBar = () => {
  const navigate = useNavigate();
  const form = useForm({ defaultValues: defaultPatient });
  const [inputValue, setInputValue] = useState("");
  const [calculators, setCalculators] = useState([]);
  const [selectedCalculator, setSelectedCalculator] = useState(null);
  
  const { strategyProps } = usePatientSearchStrategy();

  useEffect(() => {
    if (inputValue && inputValue.length >= 3) {
      const matchingCalculators = searchCalculatorsMultilingual(inputValue);
      setCalculators(matchingCalculators.map(calc => ({
        ...calc,
        type: "calculator"
      })));
    } else {
      setCalculators([]);
    }
  }, [inputValue]);

  const combinedOptions = useMemo(() => {
    const patientOptions = strategyProps.patients || [];
    return [...patientOptions, ...calculators];
  }, [strategyProps.patients, calculators]);

  const handleAsyncSearch = async (searchText) => {
    const patientResults = await strategyProps.onAsyncSearch(searchText);
      
    const calculatorResults = searchText && searchText.length >= 3
      ? searchCalculatorsMultilingual(searchText).map(calc => ({
          ...calc,
          type: "calculator"
        }))
      : [];
      
    return [...patientResults, ...calculatorResults];
  };

  
  const selectProps = strategyProps.onAsyncSearch
    ? { onAsyncSearch: handleAsyncSearch }
    : { list: combinedOptions, loading: strategyProps.loading };

  const handleOptionSelect = (option) => {
    if (!option) return;

    if (option.type === "calculator") {
      setSelectedCalculator(option);
      return;
    }

    const patient = option;
    const { id, name, surname, documentNumber, insuranceNumber } = patient;
    
    form.reset({
      id,
      name,
      surname,
      documentNumber,
      insuranceNumber,
    });

    navigate(`/patients/${id}`, { state: { patient, path: "/" } });
  };

  const getOptionLabel = (option) => {
    if (!option) return "";
    if (option.type === "calculator") return option.name;
    if (typeof option === "string") return option;
    const { name, surname, documentNumber } = option;
    return `${surname}, ${name} - ${documentNumber}`;
  };

  const renderOption = (props, option) => {
    if (option.type === "calculator") {
      return (
        <li {...props} key={`calc-${option.id}`}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalculateIcon color="primary" sx={{ mr: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" component="div">
                {option.name}
              </Typography>
            </Box>
          </Box>
        </li>
      );
    }
    
    return (
      <li {...props} key={`patient-${option.id}`}>
        <Typography variant="body1">
          {option.surname}, {option.name} - {option.documentNumber}
        </Typography>
      </li>
    );
  };

  const onInputChange = (inputValue) => {
    if (strategyProps.onAsyncSearch) return;
    setInputValue(inputValue);
  };

  const handleCloseCalculatorDialog = () => {
    setSelectedCalculator(null);
  };

  return (
    <>
      <SelectWithFilter
        placeholder="Buscar..."
        onPicked={handleOptionSelect}
        onChange={onInputChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInputProps={{
          InputProps: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          },
        }}
        groupBy={(option) => option.type === "calculator" ? "Calculadoras (by QxMD)" : "Pacientes"}
        noOptionsText=""
        loadingText="Buscando..."
        label=""
        freeSolo
        minInputLength={3}
        matcherOptions={{
          keys: ["name", "surname", "documentNumber", "insuranceNumber"]
        }}
        {...selectProps}
      />
      
      <CalculatorDialog 
        calculator={selectedCalculator}
        onClose={handleCloseCalculatorDialog}
      />
    </>
  );
};

export default SearchBar;